import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { X, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ImageUploadButton } from "../ImageUploadButton";
import { ImageGrid } from "../ImageGrid";
import { SortableImageGrid } from "../SortableImageGrid";
import { format } from "date-fns";
import type { VehicleTestImages, ServiceDetail } from "@/types/vehicleTest";
import type { FileObject } from "@/types/shared/file";
import { photoSessionSourceProps } from "../../photoSession/photoSessionButtonProps";

interface ServiceSectionProps {
  serviceImages: FileObject[];
  serviceDetails: ServiceDetail[];
  uploadingImages: Set<string>;
  onImageClick: (url: string, alt: string) => void;
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (section: keyof VehicleTestImages, index: number) => void;
  onReorder?: (newOrder: FileObject[]) => void;
  onRemoveServiceDetail: (id: string) => void;
  onEditServiceDetail: (detail: ServiceDetail) => void;
  onFileSelect: (files: File[]) => Promise<void>;
  onSelect: () => void;
  onCameraSelect: () => void;
  onRotate?: (image: any, direction: "left" | "right") => void;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
}

export const ServiceSection: React.FC<ServiceSectionProps> = ({
  serviceImages: serviceImagesProp,
  serviceDetails: serviceDetailsProp,
  uploadingImages,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onReorder,
  onRemoveServiceDetail,
  onEditServiceDetail,
  onFileSelect,
  onSelect,
  onCameraSelect,
  onRotate,
  rotatingImageId,
  getImageRotation,
}) => {
  const { language } = useLanguage();
  const lp = listingPostLabels(language);
  const sd = lp.serviceDetails;
  const sourceProps = photoSessionSourceProps(lp.images);
  const serviceImages = serviceImagesProp || [];
  const serviceDetails = serviceDetailsProp || [];
  const canAddMore = serviceImages.length < 15;

  const gridContent = onReorder ? (
    <SortableImageGrid
      images={serviceImages}
      section="serviceImages"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("serviceImages", index)}
      onReorder={onReorder}
      onRotate={onRotate}
      rotatingImageId={rotatingImageId}
      getImageRotation={getImageRotation}
    >
      {canAddMore && (
        <ImageUploadButton
          onSelect={onSelect}
          onFileSelect={onFileSelect}
          onCameraSelect={onCameraSelect}
          label={
            <>
              {sd.uploadImages}{" "}
              <span className="font-semibold">{sd.maxPerBatch}</span>
            </>
          }
          isThumbnail={false}
          disabled={false}
          isLoading={uploadingImages.size > 0}
          section="serviceImages"
          {...sourceProps}
        />
      )}
    </SortableImageGrid>
  ) : (
    <ImageGrid
      images={serviceImages}
      section="serviceImages"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("serviceImages", index)}
      showIndex={serviceImages.length > 0}
    />
  );

  return (
    <Card className="border-0">
      <CardContent className="pt-4">
        {onReorder ? (
          gridContent
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gridContent}

            {canAddMore && (
              <ImageUploadButton
                onSelect={onSelect}
                onFileSelect={onFileSelect}
                onCameraSelect={onCameraSelect}
                label={
                  <>
                    {sd.uploadImages}{" "}
                    <span className="font-semibold">{sd.maxPerBatch}</span>
                  </>
                }
                isThumbnail={false}
                disabled={false}
                isLoading={uploadingImages.size > 0}
                section="serviceImages"
                {...sourceProps}
              />
            )}
          </div>
        )}

        {serviceDetails.length > 0 && (
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">{sd.serviceDetailsLabel}</h4>
            {serviceDetails.map((detail) => (
              <div
                key={detail.id}
                className="flex items-center justify-between p-3 rounded"
              >
                <div>
                  <p className="font-medium">{detail.serviceType}</p>
                  <p className="text-xs md:text-sm text-gray-600">
                    {format(detail.date, "yyyy-MM-dd")} - {detail.mileage}{" "}
                    {sd.milesUnit}
                  </p>
                  {detail.notes && (
                    <p className="text-xs md:text-sm text-gray-500">
                      {detail.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEditServiceDetail(detail)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onRemoveServiceDetail(detail.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
